import ee from '../../services/earth-engine';
import { Lyzenga, addpSDB } from './index.js';

/**
 * @function model1Apply
 * @description Applies Model 1 (Lyzenga): log-linear regression on band ratio predictors.
 * Predictors: lnBand1, lnBand2, lnBand3 (log-transformed reflectances) + constant.
 * Always outputs: pred_depth_raw: raw depth estimate (present even if coefficients failed)
 * Additionally outputs pred_depth: calibrated depth (same as pred_depth_raw when coefs applied)
 * Uses server-side conditional (ee.Algorithms.If) to avoid JS branching on EE objects.
 * 1. Computes the log-transformed band predictors (lnBand1/2/3) via add_lyzengaPredictors.
 * 2. Applies the fitted Lyzenga coefficients to those predictors via applyLyzenga.
 * 3. Inverts the sign of the raw prediction (Lyzenga's regression yields negative depth) and adds it as pred_depth_raw/pred_depth.
 * @param {ee.Image} img - Input multispectral image with the required band names.
 * @param {Dictionary} coef1 - Lyzenga regression coefficients { c, a, b, d }.
 * @param {Array<string>} bands - Preset-mode band names [blue, green, red] passed to add_lyzengaPredictors.
 * @param {boolean} presetMode - Whether preset (fixed) coefficients are being used instead of a fitted model.
 * @param {Array<string>} dataBands - Band names to use instead of bands when presetMode is false.
 * @see {@link add_lyzengaPredictors} - computes the log-transformed band predictors.
 * @see {@link applyLyzenga} - applies coef1 to those predictors to produce pred_depth.
 * **Called by:
 * @see {ProcessingPage}
 * @memberof module:Bathymetry
 * @returns {ee.Image} Image with pred_depth_raw (always) and pred_depth
 */
export const model1Apply = (img, coef1, bands, presetMode, dataBands) => {
  const imageWithPredictors = Lyzenga.add_lyzengaPredictors(img, presetMode ? bands : dataBands);
  const depthImage = Lyzenga.applyLyzenga(imageWithPredictors, coef1);
  const predDepth = depthImage.select('pred_depth');
  return imageWithPredictors.addBands(
    [ee.Image.constant(1).rename('constant'), predDepth.rename('pred_depth_raw'), predDepth],
    null,
    true,
  );
};

/**
 * @function applyTuning
 * @description Applies a linear calibration (tuning) to a pseudo-depth band: outField = pField * m1 + m0
 * Used by Model 3 to transform each raw pseudo-SDB ratio (green and red) into
 * calibrated depth using the fitted linear coefficients.
 * @param {ee.Image} img - Image containing the input pseudo-depth band.
 * @param {string} pField - Name of the input pseudo-depth band (e.g. 'pSDB').
 * @param {string} outField - Name for the output calibrated band (e.g. 'pred_depth').
 * @param {ee.Dictionary} coefDict - Calibration coefficients { m1: scale, m0: offset }.
 * **Called by:
 * @see {model3Apply}
 * @memberof module:Bathymetry
 * @returns {ee.Image} Input image with the calibrated band added.
 */
export const applyTuning = (img, pField, outField, coefDict) => {
  const m1 = ee.Number(coefDict.get('m1'));
  const m0 = ee.Number(coefDict.get('m0'));
  return img.select(pField).multiply(m1).add(m0).rename(outField).toFloat();
};

/**
 * @function model2Apply
 * @description Applies Model 2 (Stumpf 2003): single log-ratio band (pSDB = log(n*B1)/log(n*B2))
 * calibrated to depth via linear regression.
 * Always outputs: pred_depth_raw: the uncalibrated pSDB ratio
 * Additionally outputs pred_depth: calibrated depth (pSDB * m1 + m0)
 * Uses ee.Algorithms.If to avoid JS branching on server-side values.
 * 1. Computes the pSDB log-ratio band from the chosen blue/green bands.
 * 2. Applies the fitted linear coefficients (m1, m0) to calibrate pSDB into pred_depth.
 * 3. Selects pred_depth alongside the three chosen bands.
 * @param {ee.Image} img - Input multispectral image.
 * @param {Dictionary} coef2 - Linear calibration coefficients { m1, m0 }.
 * @param {Array<string>} bands - Preset-mode band names [blue, green, red].
 * @param {boolean} presetMode - Whether preset (fixed) coefficients are being used instead of a fitted model.
 * @param {Array<string>} dataBands - Band names to use instead of bands when presetMode is false.
 * @see {@link addpSDB} - computes the pSDB log-ratio band.
 * **Called by:
 * @see {ProcessingPage}
 * @memberof module:Bathymetry
 * @returns {ee.Image} Image with pSDB, pred_depth_raw (always) and pred_depth.
 */
export const model2Apply = (img, coef2, bands, presetMode, dataBands) => {
  const chosenBands = presetMode ? bands : dataBands;
  const psdb = addpSDB(img, chosenBands).toFloat();
  const m1 = ee.Number(coef2.m1 || 0);
  const m0 = ee.Number(coef2.m0 || 0);
  const predDepth = psdb.multiply(m1).add(m0).rename('pred_depth').toFloat();
  return img.addBands([predDepth]).select(['pred_depth', chosenBands[0], chosenBands[1], chosenBands[2]]).toFloat();
};

/**
 * @function model3Apply
 * @description Applies Model 3 (Caballero & Stumpf 2019/2020): dual log-ratio blending.
 * Two pseudo-depth ratios are computed and calibrated independently:
 *   - pSDBgreen = log(n*B1) / log(n*B2)  => SDBgreen (Blue/Green, better in deeper water)
 *   - pSDBred   = log(n*B1) / log(n*B3)  => SDBred   (Blue/Red,   better in shallow water)
 * A depth-dependent blend is applied in the transition zone [Z1=2m, Z2=3.5m]:
 *   - depth <= Z1  => SDBred only
 *   - depth >= Z2  => SDBgreen only
 *   - Z1 < depth < Z2 => alpha*SDBred + beta*SDBgreen  (linear ramp)
 * Always outputs pred_depth_raw (= pSDBred). Outputs pred_depth, predG, predR,
 * alpha, beta when both coefG and coefR.
 * 1. Computes the green (blue/green) and red (blue/red) pSDB log-ratios via addpSDB.
 * 2. Calibrates each ratio into SDBgreen/SDBred using the fitted coefficients via applyTuning.
 * 3. Uses SDBred as a depth proxy to compute a linear blend weight (alpha/beta) between Z1 and Z2.
 * 4. Blends SDBred and SDBgreen by that weight, clamping to SDBred below Z1 and SDBgreen above Z2.
 * @param {ee.Image} img - Input multispectral image.
 * @param {ee.Dictionary} coefG - Green-ratio calibration coefficients { m1, m0}.
 * @param {ee.Dictionary} coefR - Red-ratio calibration coefficients { m1, m0 }.
 * @param {Array<string>} bands - Preset-mode band names [blue, green, red].
 * @param {boolean} presetMode - Whether preset (fixed) coefficients are being used instead of a fitted model.
 * @param {Array<string>} dataBands - Band names to use instead of bands when presetMode is false.
 * @see {@link addpSDB} - computes the green/red pSDB log-ratio bands.
 * @see {@link applyTuning} - calibrates each ratio into SDBgreen/SDBred.
 * **Called by:
 * @see {ProcessingPage}
 * @memberof module:Bathymetry
 * @returns {ee.Image} Blended depth image with pred_depth and diagnostic bands.
 */
export const model3Apply = (img, coefG, coefR, bands, presetMode, dataBands) => {
  const Z1 = ee.Number(2.0);
  const Z2 = ee.Number(3.5);
  const rgbBands = presetMode ? bands : dataBands;
  const pSDBGreen = addpSDB(img, [rgbBands[0], rgbBands[1]], 'pSDBgreen');
  const pSDBRed = addpSDB(img, [rgbBands[0], rgbBands[2]], 'pSDBred');
  const predDepthRaw = pSDBRed.rename('pred_depth_raw');
  const base = img.addBands([pSDBGreen, pSDBRed, predDepthRaw], null, true);
  const sdbG = applyTuning(base, 'pSDBgreen', 'SDBgreen', coefG);
  const sdbR = applyTuning(base, 'pSDBred', 'SDBred', coefR);
  const proxyDepth = sdbR;
  const beta = proxyDepth.subtract(Z1).divide(Z2.subtract(Z1)).clamp(0, 1);
  const alpha = ee.Image.constant(1).subtract(beta);
  const blended = sdbR.multiply(alpha).add(sdbG.multiply(beta));
  const pred = blended.where(proxyDepth.gte(Z2), sdbG).where(proxyDepth.lte(Z1), sdbR).rename('pred_depth').toFloat();
  return base
    .addBands([sdbG, sdbR, alpha, beta, pred], null, true)
    .select(['pred_depth', rgbBands[0], rgbBands[1], rgbBands[2]]);
};

/**
 * @function fitLinearCoefficientsSafe
 * @description Fits a linear regression (linearFit) between a pseudo-SDB predictor field
 * and observed depth ('or_depth'). Returns a safe dictionary with identity coefficients (m1=1, m0=0)
 * when the collection is empty or fitting fails, ensuring downstream model application never
 * receives null coefficients.
 * 1. Filters the samples to features with both the predictor field and 'or_depth' present.
 * 2. If there are samples, runs a linear fit (scale/offset); otherwise uses a null-valued dictionary.
 * 3. Falls back to m1=1, m0=0 (identity) whenever the fit produced null scale/offset.
 * @param {ee.FeatureCollection} fcSamples - Sampled points with predictor and 'or_depth'.
 * @param {string} xField - Name of the predictor field (e.g. 'pSDB', 'pSDBgreen').
 * **Called by:
 * @see {ProcessingPage}
 * @memberof module:Bathymetry
 * @returns {ee.Dictionary} Coefficients { m1, m0, n }
 */
export const fitLinearCoefficientsSafe = (fcSamples, xField) => {
  fcSamples = ee.FeatureCollection(fcSamples).filter(ee.Filter.notNull([xField, 'or_depth']));
  const n = fcSamples.size();
  const fit = ee.Dictionary(
    ee.Algorithms.If(
      n.gt(0),
      fcSamples.reduceColumns({ reducer: ee.Reducer.linearFit(), selectors: [xField, 'or_depth'] }),
      ee.Dictionary({ scale: null, offset: null }),
    ),
  );
  const m1 = fit.get('scale');
  const m0 = fit.get('offset');
  const m1IsNull = ee.Algorithms.IsEqual(m1, null);
  const m0IsNull = ee.Algorithms.IsEqual(m0, null);
  return ee.Dictionary({
    m1: ee.Number(m1 || 1),
    m0: ee.Number(m0 || 0),
    n: n,
  });
};
